import { UniqueIdService } from './unique-id.service';
import { Injectable } from '@angular/core';
import { Assessment } from "../models/assessment";
import { ScaleResult } from "../models/scale-result";
import { Question } from "../models/question";
import { QuestionSet } from "../models/question-set";
import { MultipleChoiceQuestion } from "../models/multiple-choice-question";
import { QuestionType } from "../models/question-type";
import { AssessmentResult } from "../models/assessment-result";
import { SingleChoiceQuestion } from 'app/shared/models/single-choice-question';
import { Scale } from '../models/scale';

// TODO: stephen would it make sense to rename this ScaleResultNode to something else as it can be confused with ScaleResult
class ScaleResultNode {
	private _children:ScaleResultNode[];
	private _data:ScaleResult;
	private _calculated:boolean;
	private _calculatorStategy:IScaleCalculatorStrategy;

	constructor(data:ScaleResult) {
		this._children = [];
		this._data = data;
		this._calculated = false;
		if (data.scale.type == "CriteriaThreshold") {
			this._calculatorStategy = new ScaleCalculatorCriteriaThresholdStrategy(data.scale);
		}
		else if (data.scale.type == "Mean") {
			this._calculatorStategy = new ScaleCalculatorMeanStrategy();
		}
		else {
			this._calculatorStategy = new ScaleCalculatorSumStrategy();
		}
	}

	get children() {
		return this._children;
	}
	set children(v:ScaleResultNode[]) {
		this._children = v;
	}
	get data() {
		return this._data;
	}
	get calculated() {
		return this._calculated;
	}

	calculateQuestion(question:Question) {
		this._data = this._calculatorStategy.calculate(this._data, question);
	}
	calculateTotal(limit?:number) {
		if (!limit) {
			limit = 0;
		}
		if (limit > 50) {
			throw new Error("Recursive limit reached.  Maximum child depth was greater than 50 nodes");
		}

		let score = this.data.score;
		if (!this.calculated && this.children.length) {
			this.children.forEach(c => c.calculateTotal(limit + 1));
			this.data.score = this.children.reduce((prev, current) => current.data.score + prev, score);
		}
		this._calculated = true;
	}
}

interface IScaleCalculatorStrategy{
	calculate(scaleResult:ScaleResult, question:Question) : ScaleResult
}

class ScaleCalculatorSumStrategy implements IScaleCalculatorStrategy {
	constructor() {

	}

	calculate(scaleResult:ScaleResult, question:Question) : ScaleResult {
		scaleResult.score += question.getComputedScore();
		return scaleResult;
	}
}

class ScaleCalculatorMeanStrategy implements IScaleCalculatorStrategy {
	
	private itemCount:number;
	private sum:number;

	constructor() {
		this.itemCount = 0;
		this.sum = 0;
	}

	calculate(scaleResult:ScaleResult, question:Question) {
		this.itemCount += 1;
		this.sum += question.getComputedScore();
		scaleResult.score = this.sum / this.itemCount;
		return scaleResult;
	}
}

class ScaleCalculatorCriteriaThresholdStrategy implements IScaleCalculatorStrategy
{
	private requiredQuestions:number;
	private requiredScoreThreshold:number;
	private currentQuestionsMatchingThreshold:number;

	constructor(scale:Scale) {
		let gradingData = scale.gradingData;

		this.requiredQuestions = gradingData.requiredQuestions || 0;
		this.requiredScoreThreshold = gradingData.requiredScoreThreshold || 0;
		this.currentQuestionsMatchingThreshold = 0;
	}

	calculate(scaleResult:ScaleResult, question:Question) : ScaleResult {
		let score = question.getComputedScore();
		if (score >= this.requiredScoreThreshold) {
			this.currentQuestionsMatchingThreshold += 1;
		}
		if (this.currentQuestionsMatchingThreshold >= this.requiredQuestions) {
			scaleResult.score = 1; // we've met the diagnostic criteria
		}
		else {
			scaleResult.score = 0;
		}
		return scaleResult;
	}
}


@Injectable()
export class AssessmentGraderService {

	constructor(private _uuid: UniqueIdService) { }

	public grade(assessment: Assessment): AssessmentResult {
		if (!assessment) {
			throw new Error("assessment is missing.  Cannot grade");
		}

		var result = new AssessmentResult();
		result.id = this._uuid.generate();
		result.assessment = Object.assign(new Assessment(), assessment);
		result.scaleResults = this.gradeScales(assessment);
		result.flaggedQuestions = this.flagQuestions(assessment);
		return result;

	}

	private flagQuestions(assessment: Assessment): Question[] {
		return assessment.question_sets.map(qs => {
			return qs.questions.map(q => {
				if (q.hasFlagCondition
					&& q.flaggedCondition.shouldFlagQuestion(q, qs)) {
					// set flag here
					q.isFlaggedForReview = true;
					return q;
				}
				return null;
			})
				.filter(q => q != null);
		})
			.filter(questions => questions.length)
			.reduce((prev, curr) => curr = curr.concat(prev), []);
	}
	
	private gradeScales(assessment: Assessment): ScaleResult[] {
		var scaleResults = assessment.scales.map(s => {
			let scaleResult = new ScaleResult();
			scaleResult.scale = s;
			scaleResult.range = s.ranges[0] ? s.ranges[0] : null;
			return scaleResult;
		});

		var resultsByScale: Map<String, ScaleResultNode> = new Map<String, ScaleResultNode>();

		// create the initial loop
		scaleResults.forEach(sr => {
			if (!resultsByScale[sr.scale.id]) {
				resultsByScale[sr.scale.id] = new ScaleResultNode(sr);
			}
		});
		// now create the children
		scaleResults.forEach(sr => {
			// now we are going to populate the trees
			if (sr.scale.hasSubScales()) {
				resultsByScale[sr.scale.id].children = sr.scale.getSubScaleIds().map(id => resultsByScale[id]);
			}
		});

		// if there are any branching or skip-logic questions we want to only grade those.
		let questionSets = assessment.getQuestionSetsForDisplay();
		questionSets.forEach(qs => {
			qs.questions.forEach(q => {
				if (this.shouldGradeQuestion(q)) {
					let resultNode:ScaleResultNode = resultsByScale[q.assessment_scale.id];
					this.gradeQuestion(q, qs, resultNode);
				}
			});
		});
		// now that the scales are graded we go through and do depth first calculation
		


		scaleResults.forEach(sr => {
			let resultNode:ScaleResultNode = resultsByScale[sr.scale.id];
			resultNode.calculateTotal(); // calculate out the tree of the item.
			sr.range = sr.scale.getRangeForScore(sr.score);
		});

		return scaleResults;
	}

	private calculateScoreWithSubScores(resultsByScale:Map<String, ScaleResult>, result:ScaleResult, limit:number) {
		if (!result.scale.hasSubScales()) {
			return result.score;
		}
		if (limit >= 50) {
			throw new Error("recursive limit reached for scale calculation.  Check scales for cycles");
		}
		// TODO: stephen if we introduced a strategy pattern here as well we could deal with different kinds
		// of sub-scal summations -- thresholds, means, averages, etc.
		let score = result.score;
		let scales = result.scale.getSubScaleIds()
						.forEach(scaleId => {
							let subScaleResult = resultsByScale[scaleId];
							score += this.calculateScoreWithSubScores(resultsByScale, subScaleResult, limit + 1);
						});
		return score;
	}

	private shouldGradeQuestion(q: Question) {
		if (q.skip_scoring) {
			return false;
		}
		if (q.type != QuestionType.MultipleChoice
			&& q.type != QuestionType.SingleChoice) {
			return false;
		}
		return true;
	}

	private gradeSingleChoice(q: SingleChoiceQuestion, qs: QuestionSet, scaleResult: ScaleResultNode) {
		let addScore = q.is_reversed ? 0 : 1;
		let score = qs.answer_start_value;

		if (q.answerForSave.isSelected) {
			// start is 0, answer is 1, reverse, answer is 0
			score += qs.answer_start_value + addScore;
		}

		// add in the multiplier... defaults to 1
		// 0 is disabled, so don't multiply.
		if (q.assessment_scale.multiplier != 0) {
			score = score * q.assessment_scale.multiplier;
		}
		q.setComputedScore(score);
		scaleResult.calculateQuestion(q);
	}
	private gradeMultipleChoice(q: MultipleChoiceQuestion, qs: QuestionSet, scaleResult: ScaleResultNode) {
		// we are only allowing multiple choice questions to be graded right now
		let labels = q.CustomLabels.length ? q.CustomLabels : qs.labels;
		let totalAnswers = labels.length;
		let score = labels.indexOf(q.answer);
		// if we can't find the label we have issues


		if (score < 0) {
			console.error("Scale not found for question " + q.id);
			return;
		}
		let label = labels[score];

		// if the label is something like Not Applicable
		// we want to skip over scoring that question.
		if (label.skipScoring) {
			return;
		}

		// grab the actual scale
		if (q.is_reversed) {
			// since the startValue is inclusive we must subtract one from the count
			let endValue = qs.answer_start_value + totalAnswers - 1;
			let interval = score - qs.answer_start_value;
			score = endValue - score;
		}
		else {
			score = qs.answer_start_value + score;
		}

		// add in the multiplier... defaults to 1
		// 0 is disabled, so don't multiply.
		if (q.assessment_scale.multiplier != 0) {
			score = score * q.assessment_scale.multiplier;
		}
		q.setComputedScore(score);
		scaleResult.calculateQuestion(q);
	}
	private gradeQuestion(question: Question, qs: QuestionSet, scaleResult: ScaleResultNode) {
		if (question.type == QuestionType.MultipleChoice) {
			return this.gradeMultipleChoice(question as MultipleChoiceQuestion, qs, scaleResult);
		}
		else if (question.type == QuestionType.SingleChoice) {
			return this.gradeSingleChoice(question as SingleChoiceQuestion, qs, scaleResult);
		}
	}

}



// WEBPACK FOOTER //
// ./src/app/shared/services/assessment-grader.service.ts